'use client';

import { useState, useEffect } from 'react';
import { Fingerprint, Check } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { useAuth } from '@/components/auth-provider';
import { BottomSheet } from '@/components/bottom-sheet';
import { useT } from '@/components/i18n-provider';
import { useAnalytics } from '@/hooks/use-analytics';

const DISMISSED_KEY = 'passkey-prompt-dismissed';
const REGISTERED_KEY = 'robutler_passkey_registered';
const ONBOARDING_KEY = 'robutler_onboarding_done';
const SHOW_DELAY_MS = 600_000; // 10 minutes

export function PasskeyPrompt() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const t = useT();
  const { trackPromptResponse } = useAnalytics();

  useEffect(() => {
    if (!user) return;
    // Guests are ephemeral throwaway accounts — never nudge them to register a
    // biometric/passkey login. (Runtime accountType can be 'guest' though the
    // AuthProvider type narrows it to human|agent; see auth-provider.tsx.)
    if ((user.accountType as 'human' | 'agent' | 'guest') === 'guest') return;
    if (typeof window === 'undefined' || !window.PublicKeyCredential) return;
    if (localStorage.getItem(REGISTERED_KEY) === '1') return;
    if (localStorage.getItem(ONBOARDING_KEY) !== '1') return;

    const dismissed = localStorage.getItem(DISMISSED_KEY);
    if (dismissed) {
      const ts = parseInt(dismissed, 10);
      if (Date.now() - ts < 7 * 24 * 60 * 60 * 1000) return;
    }

    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [user]);

  const handleSetup = async () => {
    trackPromptResponse('passkey', 'accept');
    setRegistering(true);
    setError(null);
    try {
      const { startRegistration } = await import('@simplewebauthn/browser');
      const optionsRes = await fetch('/api/auth/webauthn/register');
      if (!optionsRes.ok) throw new Error(t('settings.failedToRegisterPasskey'));
      const options = await optionsRes.json();
      const regResp = await startRegistration({ optionsJSON: options });
      const verifyRes = await fetch('/api/auth/webauthn/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(regResp),
      });
      const result = await verifyRes.json();
      if (result.verified) {
        try { localStorage.setItem(REGISTERED_KEY, '1'); } catch {}
        setDone(true);
        setTimeout(() => setVisible(false), 2000);
      } else {
        throw new Error(result.error || t('settings.passkeyRegistrationFailed'));
      }
    } catch (err: any) {
      if (err.name !== 'NotAllowedError') {
        setError(err.message || t('settings.failedToRegisterPasskey'));
      }
    } finally {
      setRegistering(false);
    }
  };

  const handleDismiss = () => {
    trackPromptResponse('passkey', 'dismiss');
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    setVisible(false);
  };

  return (
    <BottomSheet open={visible} onClose={handleDismiss} desktopStackSlot="default">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <Fingerprint className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          {done ? (
            <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 font-medium py-1">
              <Check className="h-4 w-4" />
              {t('passkey.saved')}
            </div>
          ) : (
            <>
              <p className="text-sm font-semibold">{t('passkey.enableBiometric')}</p>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                {t('passkey.description')}
              </p>
              {error && (
                <p className="text-xs text-destructive mt-1">{error}</p>
              )}
              <div className="flex gap-2 mt-3">
                <button
                  onClick={handleSetup}
                  disabled={registering}
                  className="btn-primary text-xs px-4 py-1.5 rounded-lg flex items-center gap-1.5"
                >
                  {registering ? <Spinner size="sm" /> : null}
                  {registering ? t('passkey.settingUp') : t('passkey.setUp')}
                </button>
                <button
                  onClick={handleDismiss}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors px-3 py-1.5"
                >
                  {t('passkey.notNow')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </BottomSheet>
  );
}
